import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

import AttendanceCard from "./AttendanceCard";
import MonthlyStats from "./MonthlyStats";
import CalendarView from "./CalendarView";
import RecentActivity from "./RecentActivity";
import CompanyAttendance from "./CompanyAttendance";
import AttendanceAnalytics from "./AttendanceAnalytics";
import RegularisationModal from "./RegularisationModal";
import RegularisationRequests from "./RegularisationRequests";
import { API_URL } from '../../api/api';
import { UserCheck, Users, BarChart3, FileText, Clock, Filter, FilePenLine } from "lucide-react";
import { T, PageHeader, Tabs, Select, SecondaryButton } from "../../components/em/ui";
import "./em-attendance.css";

/* Summary period filter. Values are the `period` the API resolves against a
   real calendar (backend services/timezone.js) — the client never computes the
   date range itself, so the label and the numbers can't disagree. */
const PERIOD_OPTIONS = [
  { value: "week", label: "Weekly" },
  { value: "month", label: "Monthly" },
  { value: "quarter", label: "Quarterly" },
  { value: "year", label: "Yearly" },
];

/* ---------- Main Component ---------- */
const Attendance = () => {
  const userData = JSON.parse(localStorage.getItem("userdata") || "{}");
  const token = localStorage.getItem("token");

  const userId = userData.id;
  // Role is stored mixed-case (e.g. "Admin"). Normalise once for comparisons.
  const role = String(
    userData.role || localStorage.getItem("role") || ""
  ).toLowerCase();

  const [activeTab, setActiveTab] = useState("my");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [todayRec, setTodayRec] = useState(null);
  const [stats, setStats] = useState({});
  const [calendarData, setCalendarData] = useState([]);
  const [recent, setRecent] = useState([]);
  const [showRegularise, setShowRegularise] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [period, setPeriod] = useState("month");
  // Bumped after a successful submit to make the regularisation list refetch.
  const [regularisationRefresh, setRegularisationRefresh] = useState(0);

  const apiBase = `${API_URL}/api/attendance`;
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  // Centralised auth-error handler — bounce to login on 401.
  const handleAuthError = (err) => {
    if (err?.response?.status === 401) {
      localStorage.clear();
      window.location.assign("/login");
      return true;
    }
    return false;
  };

  const fetchMyAttendance = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const [todayRes, statsRes, calRes, recentRes] = await Promise.all([
        axios.get(`${apiBase}/today/${userId}`, authHeader),
        // Only the summary honours the period filter. The calendar is a month
        // grid with its own navigation, and Recent Activity is a fixed tail —
        // neither has a meaning for "this quarter".
        axios.get(`${apiBase}/monthly/${userId}`, { ...authHeader, params: { period } }),
        axios.get(`${apiBase}/calendar/${userId}`, authHeader),
        axios.get(`${apiBase}/recent/${userId}`, authHeader),
      ]);
      setTodayRec(todayRes.data || null);
      setStats(statsRes.data || {});
      setCalendarData(calRes.data || []);
      setRecent(recentRes.data || []);
    } catch (err) {
      console.error("Attendance fetch error", err);
      if (!handleAuthError(err)) {
        setError(
          err?.response?.data?.error || "Could not load attendance data."
        );
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, period]);

  useEffect(() => {
    if (!userId || !token) {
      window.location.href = "/login";
      return;
    }
    if (activeTab === "my") fetchMyAttendance();
  }, [fetchMyAttendance, activeTab, userId, token]);

  // Best-effort geolocation. We request coords so the backend can flag
  // off-site mark-ins (soft validation — never blocks). Failures are silent;
  // the request still goes through without lat/lng, and the backend will flag
  // it as `remote_location` only if the admin enabled location validation.
  const tryGetGeolocation = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve({});
      let done = false;
      const finish = (val) => {
        if (done) return;
        done = true;
        resolve(val);
      };
      // Hard cap so a slow/silent geolocation prompt doesn't stall mark-in.
      setTimeout(() => finish({}), 6000);
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          finish({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          }),
        () => finish({}),
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 60000 }
      );
    });

  const handleMarkIn = async () => {
    try {
      const geo = await tryGetGeolocation();
      const res = await axios.post(
        `${apiBase}/mark-in`,
        { user_id: userId, ...geo },
        authHeader
      );
      // Server may return a soft-validation warning (off-site / off-network).
      if (res.data?.warning) {
        alert(res.data.warning);
      }
      fetchMyAttendance();
    } catch (err) {
      // 409 = already marked in/out — refresh state to reflect reality.
      if (err?.response?.status === 409) {
        fetchMyAttendance();
        alert(err.response.data?.error || "Already marked.");
        return;
      }
      if (!handleAuthError(err)) {
        alert(err?.response?.data?.error || "Could not mark in.");
      }
    }
  };

  const handleMarkOut = async () => {
    try {
      await axios.post(
        `${apiBase}/mark-out`,
        { user_id: userId },
        authHeader
      );
      fetchMyAttendance();
    } catch (err) {
      if (!handleAuthError(err)) {
        alert(err?.response?.data?.error || "Could not mark out.");
      }
    }
  };

  const isAdminOrHR = role === "admin" || role === "hr";
  // Everyone can submit regularisations now: members for themselves,
  // admin/HR for any employee (Q2=a + Q4=a).
  const canRegularise = role === "member" || isAdminOrHR;

  // Admin and HR both need the employee list to pick "on behalf of".
  useEffect(() => {
    if (!isAdminOrHR) return;
    axios
      .get(`${API_URL}/api/settings/shifts/team-members`, authHeader)
      .then((r) => setEmployees(r.data || []))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminOrHR]);

  /* ---------- Render ---------- */
  const TABS = [
    { key: "my", label: "My Attendance", icon: UserCheck },
    ...(isAdminOrHR ? [{ key: "company", label: "Company Attendance", icon: Users }] : []),
    ...(isAdminOrHR ? [{ key: "analytics", label: "Analytics Dashboard", icon: BarChart3 }] : []),
    { key: "regularisation", label: "Regularisation", icon: FileText },
  ];

  return (
    <div className="attendance-page" style={{ maxWidth: 1440, margin: "0 auto", padding: "0 32px 48px" }}>
      <PageHeader icon={Clock} title="Attendance" subtitle="Track, manage and analyse team attendance" />

      <div style={{ marginBottom: 20 }}>
        <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} />
      </div>

      {/* Content */}
      {error && (
        <div
          role="alert"
          style={{
            margin: "12px 0",
            padding: "10px 14px",
            borderRadius: 12,
            border: "1px solid #fecaca",
            background: "#fef2f2",
            color: "#b91c1c",
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          ⚠ {error}
        </div>
      )}
      {loading && activeTab === "my" ? (
        <p>Loading attendance data…</p>
      ) : (
        <>
          {/* MY ATTENDANCE */}
          {activeTab === "my" && (
            <>
              <AttendanceCard
                todayRec={todayRec}
                onMarkIn={handleMarkIn}
                onMarkOut={handleMarkOut}
                refresh={fetchMyAttendance}
              />

              {/* Summary toolbar — sits above the KPI cards it controls, so the
                  period you pick reads left-to-right into the numbers below. */}
              <div className="attd-summary-bar">
                <div className="attd-period">
                  <Filter size={13} color={T.fg5} aria-hidden="true" />
                  {/* backgroundColor, not the `background` shorthand — the
                      shorthand would wipe Select's chevron background-image. */}
                  <Select
                    aria-label="Summary period"
                    value={period}
                    onChange={(e) => setPeriod(e.target.value)}
                    disabled={loading}
                    style={{ width: "auto", border: 0, backgroundColor: "transparent", padding: "8px 28px 8px 6px", font: `600 13px/1.4 ${T.font}` }}
                  >
                    {PERIOD_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Select>
                </div>

                {canRegularise && (
                  <SecondaryButton
                    icon={FilePenLine}
                    onClick={() => setShowRegularise(true)}
                    style={{ color: T.orange, borderColor: T.orange, font: `600 13px ${T.font}` }}
                  >
                    Regularise Attendance
                  </SecondaryButton>
                )}
              </div>

              <MonthlyStats
                stats={{
                  presentDays: stats.presentDays,
                  lateDays: stats.lateDays,
                  halfDays: stats.halfDays,
                  absentDays: stats.absentDays,
                  otHours: stats.otHours,
                  totalHours: stats.totalHours,
                  avgHours: stats.avgHours,
                  totalBreakHours: stats.totalBreakHours,
                }}
              />

              <div className="attendance-bottom">
                <div className="calendar-section">
                  <CalendarView data={calendarData} />
                </div>

                <div className="activity-section">
                  <RecentActivity logs={recent} />
                </div>
              </div>
            </>
          )}

          {/* COMPANY ATTENDANCE */}
          {isAdminOrHR && activeTab === "company" && (
            <CompanyAttendance />
          )}

          {/* ANALYTICS */}
          {isAdminOrHR && activeTab === "analytics" && (
            <AttendanceAnalytics />
          )}

          {/* REGULARISATION */}
          {activeTab === "regularisation" && (
            <RegularisationRequests
              canDecide={isAdminOrHR}
              onSubmitNew={canRegularise ? () => setShowRegularise(true) : null}
              refreshKey={regularisationRefresh}
            />
          )}
        </>
      )}

      {/* Regularisation submit modal */}
      {showRegularise && (
        <RegularisationModal
          employees={employees}
          onClose={() => setShowRegularise(false)}
          onSuccess={() => {
            setShowRegularise(false);
            // The list does NOT reload on its own — its fetch effect only runs
            // on mount, so submitting from the Regularisation tab (where the
            // component is already mounted) left the new row invisible until a
            // page reload. Bumping this key re-runs that fetch.
            setRegularisationRefresh((n) => n + 1);
            // No attendance refetch: a pending request doesn't change any
            // attendance row. Approving one does, and that path already reloads.
          }}
        />
      )}
    </div>
  );
};

export default Attendance;
